import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useConfig } from "../context/ConfigContext.jsx";
import { useCompanies } from "../../features/companies/hooks/useCompanies.js";
import { BottomSheet } from "./BottomSheet.jsx";
import { useInstallPrompt } from "../hooks/useInstallPrompt.js";
import { useAppBranding } from "../hooks/useAppBranding.js";
import { usePageHeading } from "../context/PageHeadingContext.jsx";
import { comboHint } from "../shortcuts/registry.js";
import { CompanyPicker } from "./CompanyPicker.jsx";

export function Header({ title, route, user, onLogout, onToggleSidebar }) {
  const config = useConfig();
  const displayTitle = title || config?.name || "Books Invoicing";
  const [isIosHintOpen, setIsIosHintOpen] = useState(false);
  const { canInstall, showIosHint, install } = useInstallPrompt();
  const { defaultCompany } = useCompanies(); // company setup lives in Settings → Companies
  useAppBranding(defaultCompany); // installed-app name/icon follow the default company
  const { heading } = usePageHeading(); // desktop breadcrumb tail for an open record, e.g. "#INV-104"

  return (
    <>
      <header className="app-header">
        {/* Desktop (≥1024px): sidebar toggle + breadcrumb "Company / Section / Page" */}
        <div className="header-desktop-left desktop-only">
          <button
            type="button"
            className="btn-icon header-menu-btn"
            onClick={onToggleSidebar}
            title={`Toggle menu (${comboHint("toggle_sidebar")})`}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          <nav className="header-breadcrumb" aria-label="Breadcrumb">
            {config?.features?.companies !== false ? <CompanyPicker variant="crumb" /> : <span className="crumb">{defaultCompany?.name || config?.name || "Books"}</span>}
            {route?.section && route.section !== route.label && <span className="crumb">{route.section}</span>}
            {heading ? (
              <>
                <button type="button" className="crumb crumb-link" onClick={heading.onBack}>{route?.label || displayTitle}</button>
                <span className="crumb crumb-current">{heading.label}</span>
              </>
            ) : (
              <span className="crumb crumb-current">{route?.label || displayTitle}</span>
            )}
          </nav>
        </div>
        <div className="header-title-box mobile-only">
          {defaultCompany?.logoUrl ? (
            <img src={defaultCompany.logoUrl} alt="" className="header-logo" style={{ width: "26px", height: "26px", objectFit: "contain", borderRadius: "6px", background: "#fff", flexShrink: 0 }} />
          ) : (
            <span className="header-logo">{config?.branding?.appLogo || "💼"}</span>
          )}
          <h1 className="header-title" title={displayTitle}>{displayTitle}</h1>
        </div>
        <div className="header-actions">
          {(canInstall || showIosHint) && (
            <button
              type="button"
              className="btn btn-primary btn-sm header-btn"
              onClick={() => (canInstall ? install() : setIsIosHintOpen(true))}
              title="Install app on this device"
              aria-label="Install app"
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <span>📲</span>
              <span className="install-text" style={{ fontSize: "0.75rem" }}>Install</span>
            </button>
          )}

          {config?.features?.companies !== false && (
            <span className="mobile-only" style={{ display: "contents" }}>
              <CompanyPicker />
            </span>
          )}

          <NavLink
            to="/settings"
            className={({ isActive }) => `btn btn-secondary btn-sm header-btn mobile-only ${isActive ? "active" : ""}`}
            title="Settings"
            aria-label="Settings"
            style={{ textDecoration: "none" }}
          >
            ⚙️
          </NavLink>

          {user && (
            <button
              type="button"
              className="btn btn-secondary btn-sm header-btn"
              onClick={onLogout}
              title={user.email || "Logout"}
              aria-label="Logout"
            >
              <span className="header-user desktop-only">{user.email}</span>
              <span className="logout-icon">🚪</span>
              <span className="logout-text">Logout</span>
            </button>
          )}
        </div>
      </header>

      <BottomSheet isOpen={isIosHintOpen} onClose={() => setIsIosHintOpen(false)} title="📲 Install on iPhone / iPad">
        <ol style={{ margin: 0, paddingLeft: "1.2rem", lineHeight: 1.8, fontSize: "0.92rem" }}>
          <li>Open this page in <strong>Safari</strong>.</li>
          <li>Tap the <strong>Share</strong> button (square with an arrow ⬆️).</li>
          <li>Choose <strong>Add to Home Screen</strong>, then tap <strong>Add</strong>.</li>
        </ol>
        <div style={{ fontSize: "0.8rem", color: "var(--text-dim)", marginTop: "0.75rem" }}>
          The app then opens full-screen from your home screen, like a normal app.
        </div>
      </BottomSheet>
    </>
  );
}

export default Header;
